import { hex, mix, type Col } from "../art/pal.ts";
import type { TrainerId } from "../data/trainers.ts";
import { Spr } from "./spr.ts";

/**
 * Chibi character painter: 16×26 frames, four facings, four-step walk cycle. Every trainer,
 * villager and the player come from the same rig with different outfits, so the street reads as
 * one cast. Frames are anchored at the feet (ax, ay).
 */
export interface Outfit {
  skin: Col;
  hair: Col;
  hairStyle: "short" | "long" | "spiky" | "bun" | "bob";
  hat?: { color: Col; kind: "cap" | "beanie" | "band" };
  shirt: Col;
  trim?: Col;
  pants: Col;
  shoes: Col;
  pack?: Col;
}

export type Facing = 0 | 1 | 2 | 3; // up, down, right, left (matches SaveState.dir)

const INK = hex("#1c1530");
const W = 16, H = 26;
export const ACTOR_AX = 8;
export const ACTOR_AY = 25;

function ramp(c: Col): [Col, Col, Col] {
  return [mix(c, hex("#fff6d8"), 0.28), c, mix(c, hex("#2a1a48"), 0.38)];
}

const cache = new Map<string, ImageData>();

export function actorFrame(key: string, o: Outfit, dir: Facing, frame: number): ImageData {
  const k = `${key}:${dir}:${frame & 3}`;
  const hit = cache.get(k);
  if (hit) return hit;
  const img = paint(o, dir, frame & 3);
  cache.set(k, img);
  return img;
}

function paint(o: Outfit, dir: Facing, frame: number): ImageData {
  const s = new Spr(W, H);
  const skin = ramp(o.skin), hair = ramp(o.hair), shirt = ramp(o.shirt), pants = ramp(o.pants), shoes = ramp(o.shoes);
  const step = frame === 1 ? 1 : frame === 3 ? -1 : 0; // which leg leads
  const bob = frame === 1 || frame === 3 ? 1 : 0;
  const side = dir === 2 || dir === 3;
  const back = dir === 0;

  // ---- Legs & shoes ------------------------------------------------------------------------
  const legTop = 19 + bob;
  if (!side) {
    const legs: [number, number][] = [[5, step > 0 ? 1 : step < 0 ? -1 : 0], [9, step < 0 ? 1 : step > 0 ? -1 : 0]];
    for (const [lx, dy] of legs) {
      const len = 4 + dy;
      s.fill(lx, legTop, 3, len, pants[1]);
      s.vline(lx, legTop, len, pants[0]);
      s.vline(lx + 2, legTop, len, pants[2]);
      s.fill(lx, legTop + len, 3, 2, shoes[1]);
      s.hline(lx, legTop + len, 3, shoes[0]);
      s.hline(lx, legTop + len + 1, 3, shoes[2]);
    }
  } else {
    const f = dir === 2 ? 1 : -1;
    const spread = step === 0 ? 0 : 2;
    const legs: [number, Col[]][] = [[7 - spread * f, [pants[2], pants[1], pants[2]]], [7 + spread * f, [pants[0], pants[1], pants[2]]]];
    for (const [lx, c] of legs) {
      s.fill(lx, legTop, 3, 4, c[1]!);
      s.vline(lx, legTop, 4, c[0]!);
      s.fill(lx + (f > 0 ? 0 : -1), legTop + 4, 4, 2, shoes[1]);
      s.hline(lx + (f > 0 ? 0 : -1), legTop + 4, 4, shoes[0]);
    }
  }

  // ---- Body ---------------------------------------------------------------------------------
  const by = 13 + bob;
  const bw = side ? 8 : 10;
  const bx = side ? 4 : 3;
  for (let y = by; y < by + 7; y++) {
    for (let x = bx; x < bx + bw; x++) {
      const u = (x - bx) / (bw - 1);
      s.p(x, y, u < 0.25 ? shirt[0] : u > 0.78 ? shirt[2] : shirt[1]);
    }
  }
  if (o.trim) {
    if (!side && !back) {
      s.vline(7, by, 7, o.trim);
      s.vline(8, by, 7, o.trim);
    } else s.hline(bx, by + 5, bw, o.trim);
  }
  // Shorts/belt line.
  s.hline(bx, by + 6, bw, pants[1]);

  // Arms swing opposite to legs.
  if (!side) {
    const swing = step;
    for (const [ax, dy, c] of [[1, -swing, 0], [13, swing, 2]] as const) {
      const ay = by + 1 + (dy > 0 ? 1 : 0);
      s.fill(ax + (ax === 1 ? 1 : 0), ay, 2, 5 - (dy < 0 ? 1 : 0), shirt[c]);
      s.fill(ax + (ax === 1 ? 1 : 0), ay + 5 - (dy < 0 ? 1 : 0), 2, 2, skin[1]);
    }
  } else {
    const f = dir === 2 ? 1 : -1;
    const ax = 7 + step * f * 2;
    s.fill(ax, by + 1, 2, 5, shirt[2]);
    s.fill(ax, by + 6, 2, 2, skin[1]);
  }

  // Backpack.
  if (o.pack) {
    const pk = ramp(o.pack);
    if (back) {
      s.fill(4, by, 8, 7, pk[1]);
      s.hline(4, by, 8, pk[0]);
      s.fill(5, by + 3, 6, 1, pk[2]);
      s.p(7, by + 4, hex("#ffd84a"));
    } else if (side) {
      const f = dir === 2 ? -1 : 1;
      s.fill(f > 0 ? 11 : 1, by, 4, 6, pk[1]);
      s.vline(f > 0 ? 11 : 4, by, 6, pk[0]);
    } else {
      s.vline(3, by, 3, pk[2]);
      s.vline(12, by, 3, pk[2]);
    }
  }

  // ---- Head ---------------------------------------------------------------------------------
  const hy = 2 + bob;
  const hx = 2;
  const headW = 12, headH = 11;
  for (let y = 0; y < headH; y++) {
    const inset = y === 0 || y === headH - 1 ? 2 : y === 1 || y === headH - 2 ? 1 : 0;
    for (let x = inset; x < headW - inset; x++) {
      const u = x / (headW - 1);
      s.p(hx + x, hy + y, u < 0.2 ? skin[0] : u > 0.82 ? skin[2] : skin[1]);
    }
  }
  // Hair.
  const hairPx = (x: number, y: number) => {
    const u = (x - hx) / (headW - 1);
    s.p(x, y, u < 0.3 ? hair[0] : u > 0.75 ? hair[2] : hair[1]);
  };
  if (back) {
    for (let y = hy; y < hy + headH - 1; y++) for (let x = hx; x < hx + headW; x++) if (s.alpha(x, y)) hairPx(x, y);
    if (o.hairStyle === "long" || o.hairStyle === "bob") for (let y = hy + headH - 1; y < hy + headH + (o.hairStyle === "long" ? 4 : 1); y++) for (let x = hx + 1; x < hx + headW - 1; x++) hairPx(x, y);
    if (o.hairStyle === "bun") { s.oval(hx + 6, hy - 1, 2.5, 2, hair[1]); }
  } else if (side) {
    const f = dir === 2 ? 1 : -1;
    for (let y = hy; y < hy + 5; y++) for (let x = hx; x < hx + headW; x++) if (s.alpha(x, y)) hairPx(x, y);
    // Back of the head covered in hair.
    for (let y = hy + 5; y < hy + headH - 1; y++) {
      for (let k = 0; k < 6; k++) {
        const x = f > 0 ? hx + k : hx + headW - 1 - k;
        if (s.alpha(x, y)) hairPx(x, y);
      }
    }
    if (o.hairStyle === "long") for (let y = hy + headH - 1; y < hy + headH + 4; y++) for (let k = 0; k < 5; k++) hairPx(f > 0 ? hx + 1 + k : hx + headW - 2 - k, y);
    // Eye and nose on the leading side.
    const ex = f > 0 ? hx + 9 : hx + 2;
    s.fill(ex, hy + 6, 1, 2, INK);
    s.p(ex, hy + 5, hair[2]);
    s.p(f > 0 ? hx + 11 : hx, hy + 8, skin[2]);
    s.p(f > 0 ? hx + 8 : hx + 3, hy + 9, hex("#ff9aa8"));
  } else {
    // Front: fringe.
    for (let y = hy; y < hy + 4; y++) for (let x = hx; x < hx + headW; x++) if (s.alpha(x, y)) hairPx(x, y);
    for (const x of [hx, hx + 1, hx + headW - 2, hx + headW - 1]) for (let y = hy + 4; y < hy + 8; y++) if (s.alpha(x, y)) hairPx(x, y);
    if (o.hairStyle === "spiky") for (let x = hx + 2; x < hx + headW - 2; x += 3) { s.p(x, hy + 4, hair[1]); s.p(x + 1, hy - 1, hair[1]); }
    else s.p(hx + 4, hy + 4, hair[1]);
    if (o.hairStyle === "long") for (let y = hy + 8; y < hy + headH + 4; y++) { hairPx(hx, y); hairPx(hx + 1, y); hairPx(hx + headW - 2, y); hairPx(hx + headW - 1, y); }
    if (o.hairStyle === "bun") s.oval(hx + 6, hy - 1, 2.5, 2, hair[1]);
    // Eyes with a catch light, blush.
    for (const ex of [hx + 3, hx + 8]) {
      s.fill(ex, hy + 6, 1, 2, INK);
      s.p(ex + 1, hy + 6, INK);
      s.p(ex + 1, hy + 7, INK);
      s.p(ex, hy + 6, hex("#fff8e8"));
    }
    s.p(hx + 2, hy + 9, hex("#ff9aa8"));
    s.p(hx + 10, hy + 9, hex("#ff9aa8"));
    s.p(hx + 6, hy + 9, skin[2]);
  }

  // Hat.
  if (o.hat) {
    const hc = ramp(o.hat.color);
    if (o.hat.kind === "band") {
      s.hline(hx, hy + 3, headW, hc[1]);
      s.hline(hx + 1, hy + 3, 3, hc[0]);
    } else {
      for (let y = hy - 1; y < hy + 4; y++) {
        for (let x = hx; x < hx + headW; x++) {
          const inset = y === hy - 1 ? 2 : y === hy ? 1 : 0;
          if (x < hx + inset || x >= hx + headW - inset) continue;
          const u = (x - hx) / (headW - 1);
          s.p(x, y, u < 0.3 ? hc[0] : u > 0.75 ? hc[2] : hc[1]);
        }
      }
      if (o.hat.kind === "cap") {
        if (dir === 1) { s.hline(hx + 1, hy + 4, headW - 2, hc[2]); s.p(hx + 5, hy, hex("#fff6e6")); s.p(hx + 6, hy, hex("#fff6e6")); }
        else if (side) { const f = dir === 2 ? 1 : -1; s.fill(f > 0 ? hx + 9 : hx - 2, hy + 3, 5, 1, hc[2]); }
        else s.hline(hx + 2, hy + 3, headW - 4, hc[2]);
      } else {
        s.hline(hx, hy + 3, headW, hc[2]);
        s.p(hx + 6, hy - 2, hex("#fff6e6"));
      }
    }
  }

  s.outline(INK, 0.25);
  return s.img;
}

/** Outfits for the cast. */
export const PLAYER_OUTFIT: Outfit = {
  skin: hex("#f6c9a0"),
  hair: hex("#5a3a2e"),
  hairStyle: "short",
  hat: { color: hex("#ff5a6a"), kind: "cap" },
  shirt: hex("#f4f1ea"),
  trim: hex("#3d7ff0"),
  pants: hex("#34426e"),
  shoes: hex("#ff7f50"),
  pack: hex("#ffb84a"),
};

export const STICKER_OUTFIT: Outfit = { ...PLAYER_OUTFIT, shirt: hex("#ffd84a"), trim: hex("#ff5a6a") };

/** Regional outfits from the Frontier, Harbor and Bridgeport stalls (pure style). */
export const REGION_OUTFITS: Record<"dunewrap" | "neonjacket" | "blossomcoat", Outfit> = {
  dunewrap: { ...PLAYER_OUTFIT, hat: { color: hex("#f2d49a"), kind: "band" }, shirt: hex("#e8a050"), trim: hex("#fff0c0"), pants: hex("#8a5a3a"), shoes: hex("#5a3a2e"), pack: hex("#c9862a") },
  neonjacket: { ...PLAYER_OUTFIT, hat: { color: hex("#35c6e6"), kind: "cap" }, shirt: hex("#2a2350"), trim: hex("#ff4fd8"), pants: hex("#1c1530"), shoes: hex("#35c6e6"), pack: hex("#9b5cf2") },
  blossomcoat: { ...PLAYER_OUTFIT, hat: { color: hex("#ff9ab4"), kind: "beanie" }, shirt: hex("#ffd0dc"), trim: hex("#e8537a"), pants: hex("#4a3a6a"), shoes: hex("#f4f1ea"), pack: hex("#4fae50") },
};

/** The outfit a scout wears: their pick, else the Sticker Pack once bought, else the default look. */
export function wornOutfit(c: { sticker: boolean; wear?: string }): { key: string; outfit: Outfit } {
  const wear = c.wear || (c.sticker ? "sticker" : "base");
  if (wear === "sticker") return { key: "player-s", outfit: STICKER_OUTFIT };
  const region = REGION_OUTFITS[wear as keyof typeof REGION_OUTFITS];
  return region ? { key: `player-${wear}`, outfit: region } : { key: "player", outfit: PLAYER_OUTFIT };
}

/** Trainer outfits: one silhouette rig, distinct reads. */
export const TRAINER_OUTFITS: Record<TrainerId, Outfit> = {
  tavi: { skin: hex("#f2c29a"), hair: hex("#2a2340"), hairStyle: "spiky", hat: { color: hex("#40c4b0"), kind: "band" }, shirt: hex("#ffd84a"), trim: hex("#2a2340"), pants: hex("#3a3f6e"), shoes: hex("#ffffff") },
  pella: { skin: hex("#e8b48a"), hair: hex("#c2553a"), hairStyle: "long", shirt: hex("#9b5cf2"), trim: hex("#ffd84a"), pants: hex("#5a3a2e"), shoes: hex("#3a2a2a"), pack: hex("#ff9ab4") },
  brin: { skin: hex("#c98e68"), hair: hex("#1c1530"), hairStyle: "short", hat: { color: hex("#3d7ff0"), kind: "cap" }, shirt: hex("#ff9950"), trim: hex("#ffffff"), pants: hex("#2a3a5a"), shoes: hex("#1c1530") },
  sable: { skin: hex("#f6d0ae"), hair: hex("#f2e6c8"), hairStyle: "bob", shirt: hex("#e8537a"), trim: hex("#ffffff"), pants: hex("#2a2340"), shoes: hex("#e8537a") },
  ilex: { skin: hex("#e2a47c"), hair: hex("#4a3a2e"), hairStyle: "short", hat: { color: hex("#4fcf62"), kind: "beanie" }, shirt: hex("#35c6e6"), trim: hex("#ffd84a"), pants: hex("#3a3f6e"), shoes: hex("#ff7f50"), pack: hex("#8a5a3a") },
  vell: { skin: hex("#d9a882"), hair: hex("#ffffff"), hairStyle: "long", hat: { color: hex("#6a3160"), kind: "beanie" }, shirt: hex("#3a2a5a"), trim: hex("#ffc84a"), pants: hex("#1c1530"), shoes: hex("#6a3160") },
  rook: { skin: hex("#f0c8a0"), hair: hex("#7a4a2a"), hairStyle: "spiky", shirt: hex("#4f8cff"), trim: hex("#ffffff"), pants: hex("#1c1530"), shoes: hex("#ffd84a") },
  mica: { skin: hex("#b8805e"), hair: hex("#2a1a1a"), hairStyle: "bun", shirt: hex("#f4f1ea"), trim: hex("#9b5cf2"), pants: hex("#4a3a6a"), shoes: hex("#2a2340"), pack: hex("#c9862a") },
  nix: { skin: hex("#e8c0a0"), hair: hex("#58e0cf"), hairStyle: "bob", hat: { color: hex("#1c1530"), kind: "cap" }, shirt: hex("#1d2544"), trim: hex("#58e0cf"), pants: hex("#141a2e"), shoes: hex("#58e0cf") },
  kade: { skin: hex("#c98e68"), hair: hex("#3a2a2a"), hairStyle: "short", hat: { color: hex("#e8b870"), kind: "band" }, shirt: hex("#e87a3c"), trim: hex("#fff0c8"), pants: hex("#7a5238"), shoes: hex("#3a2a2a"), pack: hex("#d9ae6a") },
  frida: { skin: hex("#f6d2b8"), hair: hex("#f4f4ff"), hairStyle: "long", hat: { color: hex("#90d9f1"), kind: "beanie" }, shirt: hex("#4fa1cd"), trim: hex("#ffffff"), pants: hex("#244b8e"), shoes: hex("#e8f6ff") },
  morrow: { skin: hex("#d9a882"), hair: hex("#354a34"), hairStyle: "spiky", hat: { color: hex("#9a4686"), kind: "cap" }, shirt: hex("#5f7a48"), trim: hex("#d27ab4"), pants: hex("#3a3a2a"), shoes: hex("#243426") },
  quartz: { skin: hex("#e8c0a0"), hair: hex("#ae92f2"), hairStyle: "bun", shirt: hex("#7d66d2"), trim: hex("#a4f3ea"), pants: hex("#3b297a"), shoes: hex("#e4d2ff"), pack: hex("#5fd6cf") },
  cinder: { skin: hex("#b07a5a"), hair: hex("#ff8a3c"), hairStyle: "spiky", hat: { color: hex("#2f2c44"), kind: "band" }, shirt: hex("#2f2c44"), trim: hex("#ff9a3c"), pants: hex("#1e1620"), shoes: hex("#e8582c") },
  opal: { skin: hex("#e0b090"), hair: hex("#2a3a58"), hairStyle: "bob", hat: { color: hex("#ffd060"), kind: "band" }, shirt: hex("#56647e"), trim: hex("#5ae8d0"), pants: hex("#303a50"), shoes: hex("#1a2030"), pack: hex("#82d84a") },
  nami: { skin: hex("#c88a64"), hair: hex("#2a1a1a"), hairStyle: "long", shirt: hex("#f2665c"), trim: hex("#fff4d4"), pants: hex("#3aa4c8"), shoes: hex("#ffd0c0") },
  vex: { skin: hex("#f0d0b8"), hair: hex("#ff5ad8"), hairStyle: "spiky", hat: { color: hex("#1a1236"), kind: "cap" }, shirt: hex("#1a1236"), trim: hex("#3ef0e0"), pants: hex("#2a1a4a"), shoes: hex("#3ef0e0") },
  lune: { skin: hex("#d8b4a0"), hair: hex("#eef0ff"), hairStyle: "long", hat: { color: hex("#f5d86a"), kind: "band" }, shirt: hex("#343aa0"), trim: hex("#fff0a0"), pants: hex("#1f2064"), shoes: hex("#eef6ff"), pack: hex("#eef6ff") },  juno: { skin: hex("#e2a47c"), hair: hex("#c2553a"), hairStyle: "bun", hat: { color: hex("#e8b870"), kind: "band" }, shirt: hex("#e87a3c"), trim: hex("#ffd84a"), pants: hex("#6a4a2e"), shoes: hex("#3a2a2a"), pack: hex("#d9ae6a") },
  ash: { skin: hex("#d8c0b0"), hair: hex("#e8e4f4"), hairStyle: "long", hat: { color: hex("#2a2440"), kind: "cap" }, shirt: hex("#3a3056"), trim: hex("#9ae8c8"), pants: hex("#1e1a30"), shoes: hex("#8a7cb0") },
  kiri: { skin: hex("#a8704e"), hair: hex("#1c1530"), hairStyle: "spiky", hat: { color: hex("#4f9a45"), kind: "beanie" }, shirt: hex("#6fb84e"), trim: hex("#ffd84a"), pants: hex("#5a4a2e"), shoes: hex("#2a2340"), pack: hex("#8a5a3a") },
  hana: { skin: hex("#f6d0ae"), hair: hex("#2a1a1a"), hairStyle: "bob", hat: { color: hex("#ff9ab4"), kind: "band" }, shirt: hex("#ffc6d8"), trim: hex("#e8537a"), pants: hex("#6a3160"), shoes: hex("#ffffff") },
  osric: { skin: hex("#c98e68"), hair: hex("#b8b0a0"), hairStyle: "short", hat: { color: hex("#8a6414"), kind: "band" }, shirt: hex("#6a5a40"), trim: hex("#f5d86a"), pants: hex("#3a3020"), shoes: hex("#2a2016"), pack: hex("#a99d92") },
  // Remy Repost changes with the story: next-door hoodie, then KOL Collective merch, then his own jacket again.
  remy1: { skin: hex("#e8b48a"), hair: hex("#e8d06a"), hairStyle: "spiky", hat: { color: hex("#4fae50"), kind: "cap" }, shirt: hex("#4fae50"), trim: hex("#ffffff"), pants: hex("#34426e"), shoes: hex("#ffffff"), pack: hex("#ff9950") },
  remy2: { skin: hex("#e8b48a"), hair: hex("#e8d06a"), hairStyle: "spiky", hat: { color: hex("#ffc84a"), kind: "cap" }, shirt: hex("#6a31a0"), trim: hex("#ffc84a"), pants: hex("#1c1530"), shoes: hex("#ffc84a"), pack: hex("#6a31a0") },
  remy3: { skin: hex("#e8b48a"), hair: hex("#e8d06a"), hairStyle: "spiky", shirt: hex("#3d7ff0"), trim: hex("#4fae50"), pants: hex("#34426e"), shoes: hex("#ffffff"), pack: hex("#ff9950") },
  // The KOL Collective: purple and gold merch, one look each.
  milo: { skin: hex("#f2c29a"), hair: hex("#2a1a1a"), hairStyle: "short", hat: { color: hex("#ffc84a"), kind: "band" }, shirt: hex("#6a31a0"), trim: hex("#fff0a8"), pants: hex("#3a2a5a"), shoes: hex("#ffc84a") },
  lexi: { skin: hex("#c98e68"), hair: hex("#ff6f91"), hairStyle: "long", shirt: hex("#ffc84a"), trim: hex("#6a31a0"), pants: hex("#1c1530"), shoes: hex("#6a31a0"), pack: hex("#ff6f91") },
  theo: { skin: hex("#e2a47c"), hair: hex("#4a3a2e"), hairStyle: "bob", hat: { color: hex("#6a31a0"), kind: "beanie" }, shirt: hex("#2a2350"), trim: hex("#ffc84a"), pants: hex("#6a31a0"), shoes: hex("#2a2350") },
  cass: { skin: hex("#b8805e"), hair: hex("#f2e6c8"), hairStyle: "bun", shirt: hex("#6a31a0"), trim: hex("#ff5a6a"), pants: hex("#1c1530"), shoes: hex("#ff5a6a"), pack: hex("#ffc84a") },
  kole: { skin: hex("#f6d0ae"), hair: hex("#ffd84a"), hairStyle: "short", hat: { color: hex("#ffc84a"), kind: "cap" }, shirt: hex("#ffc84a"), trim: hex("#6a31a0"), pants: hex("#6a31a0"), shoes: hex("#fff0a8"), pack: hex("#fff0a8") },
};


function hsl(h: number, s: number, l: number): Col {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255), 255];
}

const SKINS = ["#f6c9a0", "#e8b48a", "#d9a882", "#c98e68", "#a8704e", "#7a5038"].map((h) => hex(h));
const HAIRS = ["#2a2340", "#5a3a2e", "#c2553a", "#e8c26a", "#1c1530", "#8a5cc0", "#3a7a8a"].map((h) => hex(h));
const STYLES: Outfit["hairStyle"][] = ["short", "long", "spiky", "bun", "bob"];
const HATS: (NonNullable<Outfit["hat"]>["kind"] | null)[] = ["cap", "beanie", "band", null];

/** A stable, distinct outfit for an online scout, derived from the account's hue. */
export function peerOutfit(hue: number, sticker: boolean, wear?: string): Outfit {
  const pick = <T>(arr: readonly T[], salt: number) => arr[Math.abs(Math.floor(hue * 7.3 + salt * 13)) % arr.length]!;
  const hat = pick(HATS, 3);
  // A regional outfit dresses the scout; face and hair stay their own.
  const region = wear ? REGION_OUTFITS[wear as keyof typeof REGION_OUTFITS] : undefined;
  if (region) return { skin: pick(SKINS, 1), hair: pick(HAIRS, 2), hairStyle: pick(STYLES, 5), hat: region.hat,
    shirt: region.shirt, trim: region.trim, pants: region.pants, shoes: region.shoes, pack: region.pack };
  return {
    skin: pick(SKINS, 1),
    hair: pick(HAIRS, 2),
    hairStyle: pick(STYLES, 5),
    hat: hat ? { color: hsl((hue + 180) % 360, 0.7, 0.58), kind: hat } : undefined,
    shirt: sticker ? hex("#ffd84a") : hsl(hue, 0.62, 0.56),
    trim: hsl((hue + 40) % 360, 0.5, 0.86),
    pants: hsl((hue + 210) % 360, 0.35, 0.3),
    shoes: hsl((hue + 330) % 360, 0.25, 0.18),
    pack: hsl((hue + 90) % 360, 0.55, 0.5),
  };
}
